import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  listSettlementNetworks,
  settlementNetworkForPath,
  type SettlementNetwork,
} from "./registry";
import { isSeekerAndroidApp } from "./seekerApp";
import { evmNetworkKeyForPath } from "../config/evmNetworks";

/** The EVM configuration (EVM_CONFIG) is chosen once per page load from the
 * mount path: Base at /base, DogeOS Chikyu at /dogeos. Moving between them
 * needs a new page, not just a new React session. The key loaded with this
 * page is derived the same way the config module derives it. */
const LOADED_EVM_NETWORK_KEY =
  typeof window === "undefined"
    ? "base"
    : evmNetworkKeyForPath(window.location.pathname);
function needsFullLoad(route: string): boolean {
  return evmNetworkKeyForPath(route) !== LOADED_EVM_NETWORK_KEY;
}

/** The networks this page offers. The StealthOS Android app (the Solana Seeker
 * build) offers Solana only, unless this deployment serves no Solana network. */
function offeredNetworks(): SettlementNetwork[] {
  const networks = listSettlementNetworks();
  if (!isSeekerAndroidApp()) return networks;
  const solana = networks.filter((network) => network.kind === "solana");
  return solana.length > 0 ? solana : networks;
}

/** The offered network a path opens: the path's own network when it is
 * offered, the first offered network for `/app` or for a network this page
 * does not offer, and nothing for any other path. */
function networkForPath(
  pathname: string,
  networks: SettlementNetwork[],
): SettlementNetwork | undefined {
  const routed = settlementNetworkForPath(pathname);
  const offered =
    routed && networks.find((network) => network.key === routed.key);
  if (offered) return offered;
  return routed || pathname.replace(/\/+$/, "") === "/app"
    ? networks[0]
    : undefined;
}

type Navigation = {
  network: ReturnType<typeof listSettlementNetworks>[number];
  networks: ReturnType<typeof listSettlementNetworks>;
  routeVersion: number;
  blocked: boolean;
  selectNetwork: (key: string) => boolean;
  acquireLock: () => () => void;
};
const Context = createContext<Navigation | null>(null);
const locationKey = () =>
  window.location.pathname + window.location.search + window.location.hash;

/** One app, with one mounted wallet/provider at a time. Persistent private data
 * remains owned by each chain's existing wallet/pool storage namespace. */
export function WalletNetworkProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const networks = useMemo(offeredNetworks, []);
  const [network, setNetwork] = useState(
    () => networkForPath(window.location.pathname, networks) ?? networks[0],
  );
  const [routeVersion, setRouteVersion] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const locks = useRef(new Set<symbol>());
  const currentUrl = useRef(locationKey());
  // Legacy EVM pool configuration is fixed when the bundle is loaded. Entering
  // or leaving that recovery mode needs a reload, not just a new React session.
  const previousPoolAtLoad = useRef(
    new URLSearchParams(window.location.search).get("pool") === "previous",
  );
  const acquireLock = useCallback(() => {
    const lock = Symbol();
    locks.current.add(lock);
    setBlocked(true);
    return () => {
      locks.current.delete(lock);
      setBlocked(locks.current.size > 0);
    };
  }, []);
  const selectNetwork = useCallback(
    (key: string) => {
      const next = networks.find((candidate) => candidate.key === key);
      if (!next?.enabled || locks.current.size > 0) return false;
      if (next.key === network.key) return true;
      if (
        previousPoolAtLoad.current ||
        (next.kind === "evm" && needsFullLoad(next.route))
      ) {
        window.location.assign(next.route);
        return true;
      }
      // Discard payment-link recipients, amounts and pool overrides when switching.
      window.history.pushState(null, "", next.route);
      currentUrl.current = locationKey();
      setNetwork(next);
      setRouteVersion((value) => value + 1);
      return true;
    },
    [network.key, networks],
  );
  useEffect(() => {
    const onPopState = () => {
      if (locationKey() === currentUrl.current) return;
      if (locks.current.size > 0) {
        window.history.pushState(null, "", currentUrl.current);
        return;
      }
      if (
        (new URLSearchParams(window.location.search).get("pool") ===
          "previous") !==
        previousPoolAtLoad.current
      ) {
        window.location.reload();
        return;
      }
      const next = networkForPath(window.location.pathname, networks);
      if (!next || (next.kind === "evm" && needsFullLoad(next.route))) {
        window.location.reload();
        return;
      }
      currentUrl.current = locationKey();
      setNetwork(next);
      setRouteVersion((value) => value + 1);
    };
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (locks.current.size === 0) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [networks]);
  return (
    <Context.Provider
      value={{
        network,
        networks,
        routeVersion,
        blocked,
        selectNetwork,
        acquireLock,
      }}
    >
      {children}
    </Context.Provider>
  );
}

export function useWalletNetwork() {
  return useContext(Context);
}

export function useBlockNetworkSwitch(blocked: boolean) {
  const acquireLock = useWalletNetwork()?.acquireLock;
  useLayoutEffect(() => {
    if (blocked) return acquireLock?.();
  }, [acquireLock, blocked]);
}
